import { computed, ref } from 'vue';
import { ErpEntityClient } from './client';
import { getErpEntity } from './registry';
import type { ErpBaseFilterValue } from './types';

/**
 * Prefix-cached type-ahead search for an ERP resource. Ported from the ERP app's
 * `useFetchAutocompleteRelation`, routed through the shared `ErpEntityClient`.
 *
 * Each option's label joins the resource's search fields (e.g. "ART-1103 ·
 * Mineralwasser") so NxAutocompleteInput's client-side `contains` filter matches
 * whichever field the user typed — searching by article number OR name both work.
 *
 * The `data` Map is REPLACED on every update (not mutated in place) so the
 * autocomplete's derived options recompute — a `ref<Map>` does not track `.set()`.
 */
export function useErpEntitySearch(
  resource: string,
  getBaseFilter?: () => Map<string, ErpBaseFilterValue> | undefined,
) {
  const entity = getErpEntity(resource);
  const displayColumn = entity?.displayField ?? 'name';
  const searchColumns = entity?.searchFields ?? [displayColumn];
  const searchIgnoreId = entity?.searchIgnoreId ?? false;

  const data = ref<Map<number, string>>(new Map());
  const fullyQueried: string[] = [];
  const partiallyQueried: string[] = [];
  const inFlight = ref(0);
  const isLoading = computed(() => inFlight.value > 0);

  function labelFor(row: Record<string, unknown>): string {
    const parts = searchColumns.map((f) => row[f]).filter((v) => v != null && v !== '');
    return parts.length ? parts.map(String).join(' · ') : String(row.id);
  }

  function isCached(query: string): boolean {
    if (/^\d+$/.test(query)) {
      return fullyQueried.includes(query) || partiallyQueried.includes(query);
    }
    return fullyQueried.some((prefix) => query.startsWith(prefix)) || partiallyQueried.includes(query);
  }

  async function queryFn(search: string): Promise<void> {
    // NxAutocompleteInput already lowercases the emitted search term.
    if (isCached(search)) return;
    inFlight.value++;
    try {
      const { rows, pages } = await ErpEntityClient.searchAutocomplete({
        resource,
        displayColumn,
        searchColumns,
        query: search,
        baseFilter: getBaseFilter?.(),
        searchIgnoreId,
        limit: 10,
      });
      (pages === 1 ? fullyQueried : partiallyQueried).push(search);
      const next = new Map(data.value);
      for (const row of rows) next.set(row.id, labelFor(row));
      data.value = next;
    } catch (err) {
      console.warn(`[ErpEntitySelect] search "${resource}" failed`, err);
    } finally {
      inFlight.value--;
    }
  }

  function reset(): void {
    fullyQueried.length = 0;
    partiallyQueried.length = 0;
    data.value = new Map();
    inFlight.value = 0;
  }

  return { queryFn, data, isLoading, reset };
}
